import React, { useState, useEffect, forwardRef } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Select,
  message,
  Checkbox
} from "antd";
import { DeleteFilled, LockFilled, UnlockFilled } from '@ant-design/icons';
import { HugeRTEWrapper } from '../../components/HugeRTEWrapper';
import { showVariableList } from './showVariableList';
import PhysicianAPI from '../../axios/physician/PhysicianApi';
import api from "../../axios/apiHelper";
import { TemplateTypeEnum, MODE, EXTRA_MESSAGE_CASE_STATUS_NAMES } from '../../types/enums';

const { Option } = Select;
const { TextArea } = Input;

const { CASE, MESSAGE, VISIT } = TemplateTypeEnum

const NotClickableWrapper = ({ viewMode, children }) => (
  <div style={{ pointerEvents: viewMode ? "none" : "auto" }}>{children}</div>
)

const HtmlViewer = forwardRef(({ value, ...props }, ref) => {
  const style = {
    whiteSpace: 'pre-wrap',
    border: '1px solid #d9d9d9',
    padding: '4px',
    minHeight: '32px',
    borderRadius: '2px',
    overflow: 'auto',
    ...props.style,
  }
  return (
    <div
      ref={ref}
      dangerouslySetInnerHTML={{ __html: value }}
      style={style}
      {...props}
    />
  )
});

const TelemedicineCategorySelect = ({ form, options, viewMode, required }) => {
  const onChange = (e) => form.setFieldsValue({
    teleMedicineCategoryIds: e.target.checked ? options.map(c => c._id) : [],
  });

  return (
    <NotClickableWrapper viewMode={viewMode}>
      <Form.Item
        name="teleMedicineCategoryIds"
        label={
          <div style={{ display: 'flex' }}>
            <span style={{ marginRight: 15 }}>Telemedicine Categories</span>
            <Checkbox onChange={onChange}>Select All</Checkbox>
          </div>
        }
        rules={[{ required }]}
      >
          <Select
            id="template-form-telemedicine-category-select"
            size="large"
            placeholder="Select Telemedicine Category"
            mode='multiple'
            optionFilterProp="label"
          >
            {options.map(({ _id, name }) => <Option value={_id} key={_id} label={name}>{name}</Option>)}
          </Select>
      </Form.Item>
    </NotClickableWrapper>
  )
}

const CaseStatusSelect = ({ options, viewMode, required }) => (
  <NotClickableWrapper viewMode={viewMode}>
    <Form.Item
      name="statusId"
      label="Case Status"
      rules={[{ required }]}
    >
      <Select
        id="template-form-case-status-select"
        size="large"
        placeholder="Select Case Status"
      >
        {options.map(({ _id, name }) => <Option value={_id} key={_id}>{name}</Option>)}
      </Select>
    </Form.Item>
  </NotClickableWrapper>
)

const TemplateForm = ({ mode, type, template, handleFinish, searchTemplates, isOwner, onEdit, onDelete }) => {
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [caseStatuses, setCaseStatuses] = useState([]);

  const title = `${mode} ${type[0].toUpperCase()}${type.slice(1)} Template`;
  const isViewMode = (mode === MODE.VIEW);
  const isCaseType = (type === CASE);
  const isVisitType = (type === VISIT);

  const onFinish = (values) => {
    if (mode === MODE.EDIT && onEdit) {
      form.resetFields()
      return onEdit(template._id, values)
    }

    if (mode === MODE.EDIT) {
      return PhysicianAPI.updateTemplate(template._id, values)
        .then(handleFinish)
        .then(form.resetFields)
        .then(() => searchTemplates({ type }))
        .catch(err => message.error({ content: err.response?.data?.message }))
    }

    PhysicianAPI.addTemplate(type, values)
      .then(handleFinish)
      .then(form.resetFields)
      .then(searchTemplates)
      .catch(err => message.error({ content: err.response?.data?.message }))
  }

  const handleDelete = () => {
    if (onDelete) return onDelete(template._id)

    return PhysicianAPI
      .deleteTemplate(template._id)
      .then(handleFinish)
      .then(() => searchTemplates({ type }))
      .catch(err => message.error({ content: err.response?.data?.message }))
  }

  useEffect(() => {
    api.fetchLookUp('tele-medicine-category')
      .then(res => setCategories(res.data.payload))
      .catch(() => message.error('Error loading telemedicine categories'));
    api.fetchLookUp('case-status')
      .then(res => setCaseStatuses(res.data.payload))
      .catch(() => message.error('Error loading case statuses'));
  }, [])

  useEffect(() => {
    form.setFieldsValue({
      title: template?.title,
      teleMedicineCategoryIds: template?.teleMedicineCategoryIds?.map(category => category._id),
      statusId: template?.statusId?._id,
      message: template?.message,
    });
  }, [template, form])

  const caseStatusSelectOptionsByTemplateType = {
    [CASE]: caseStatuses,
    [MESSAGE]: [
      { _id: undefined, name: 'All' },
      ...caseStatuses.filter(({ name }) => EXTRA_MESSAGE_CASE_STATUS_NAMES.includes(name)),
    ],
  }

  const caseStatusSelectOptions = caseStatusSelectOptionsByTemplateType[type] || [];

  return (
    <Form
      form={form}
      layout={"vertical"}
      onFinish={onFinish}
    >
      <Row justify='space-between'>
        <Col>
          <Typography.Title level={2}>{title}</Typography.Title>
        </Col>
        {mode === MODE.EDIT && !isVisitType && (
          <Col>
            {isOwner && (
              <Button
                id="template-form-toggle-privacy-btn"
                onClick={() => PhysicianAPI.togglePrivateTemplate(template?._id)}
                icon={template.isPrivate ? <UnlockFilled /> : <LockFilled />}
                style={{ marginRight: 15 }}
              >
                Set {template.isPrivate ? 'Public' : 'Private'}
              </Button>
            )}
            <Button id="template-form-delete-btn" onClick={handleDelete} type='danger' icon={<DeleteFilled />}>Delete</Button>
          </Col>
        )}
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true }]}
          >
            <TextArea
              id="template-form-title-textarea"
              placeholder="Enter the title for Template Message"
              readOnly={isViewMode}
              autoSize
              style={{ resize: 'none' }}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <TelemedicineCategorySelect
            form={form}
            options={categories}
            viewMode={isViewMode}
            required={isCaseType || isVisitType}
          />
        </Col>
        {!isVisitType && (
          <Col span={24}>
            <CaseStatusSelect
              options={caseStatusSelectOptions}
              viewMode={isViewMode}
              required={isCaseType}
            />
          </Col>
        )}
        <Col span={24}>
          <Form.Item
            name="message"
            label="Message"
            rules={[{ required: true }]}
          >
            {isVisitType
              ? <TextArea
                  id="template-form-message-textarea"
                  placeholder="Enter your templated message"
                  readOnly={isViewMode}
                  autoSize={{ minRows: 6 }}
                  style={{ resize: 'none' }}
                />
              : isViewMode
                ? <HtmlViewer />
                : <HugeRTEWrapper />
            }
          </Form.Item>
        </Col>
      </Row>
      <Row justify='space-between'>
        <Col>
          <Button id="template-form-variables-btn" onClick={() => showVariableList(type)}>Template Variables</Button>
        </Col>
        <Col>
          <Row justify='end'>
            {
              isViewMode
                ? <Col>
                  <Button id="template-form-close-btn" type='primary' onClick={handleFinish}>Close</Button>
                </Col>
                : <>
                  <Col style={{ marginRight: 10 }}>
                    <Button id="template-form-cancel-btn" onClick={handleFinish}>Cancel</Button>
                  </Col>
                  <Col>
                    <Button id="template-form-submit-btn" type='primary' onClick={form.submit}>{mode === 'Add' ? 'Create' : 'Save'}</Button>
                  </Col>
                </>
            }
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default TemplateForm;
